import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { confirmDialog } from 'primereact/confirmdialog';
import { Column } from 'primereact/column';
import { Declarante as DeclaranteModel } from '../../../models/Declarante';
import { DeclaranteService } from '../../../service/DeclaranteService';
import { CatalogoService } from '../../../service/CatalogoService';
import { useLazyParams } from '../../../hooks/useLazyParams';
import { InputComponent } from '../../../components/InputComponent';
import { TablaDinamica } from '../../../components/TablaDinamica';
import * as generic from '../../../components/Generico';

export const DeclaranteProveedor = ({ toast, id, catalogos, permss, tipo, soloLectura, tipoPersona }) => {

    const esTipoEspecial = ['FRM_TOYOTA', 'FRM_DIFARE'].includes(catalogos?.tipoFrmEmpresa);
    // Inicialización
    const omodel = new DeclaranteModel();
    const defaultValues = generic.funDefault(omodel.model, null);
    // Estados principales
    const [dialogDeclarante, setDialogDeclarante] = useState(false);
    const [declaranteSeleccionado, setDeclaranteSeleccionado] = useState(null);
    const menuRef = useRef(null);
    // Estados de lógica del formulario
    const [esRuc, setEsRuc] = useState(false);
    const [esCedula, setEsCedula] = useState(false);
    const [ecuador, setEcuador] = useState(false);
    const [tieneConyugue, setTieneConyugue] = useState(false);
    const [ciudades, setCiudades] = useState([]);
    const [siEstadoCivil, setSiEstadoCivil] = useState(false);
    const [titulo] = useState(tipoPersona ? "propietario" : "apoderado");
    // Servicios y Hooks
    const declaranteService = new DeclaranteService();
    const catalogoService = new CatalogoService();
    const tablaEstado = useLazyParams({ modelo: omodel.model });
    const { control, formState: { errors }, handleSubmit, reset, setError, getValues, clearErrors } = useForm({ defaultValues });
    const { inDrop, inText, inArea, inCalendar } = InputComponent({ omodel, errors, control, permss, id: getValues('id'), soloLectura });

    // Cargar datos de la tabla cuando cambien los parámetros o el id
    useEffect(() => {
        if (id) {
            loadLazyData();
        }
        // eslint-disable-next-line
    }, [tablaEstado.lazyParams, id]);

    const loadLazyData = () => {
        tablaEstado.setLoading(true);
        let _lazyParams = { ...tablaEstado.lazyParams };
        _lazyParams.idp = id;
        _lazyParams.tipo = tipo;
        declaranteService.getDeclarante({ lazyEvent: JSON.stringify(_lazyParams) }).then(res => {
            tablaEstado.setTotalRecords(res.data.total);
            tablaEstado.setData(res.data.registros);
        }).finally(() => {
            tablaEstado.setLoading(false);
        });
    };

    const openNew = () => {
        setEsRuc(false); setEsCedula(false); setEcuador(false); setTieneConyugue(false);
        if (tipoPersona) {
            defaultValues.tipo_declarante = tipoPersona;
        }
        reset(defaultValues);
        setDialogDeclarante(true);
    };

    const hideDialog = () => setDialogDeclarante(false);

    const onSubmit = (data) => {
        // Preparar data para el backend
        data.idp = id;
        data.tipo = tipo;
        data.ruc = catalogos.ruc;
        data.ec = catalogos.ecuador;
        data.casado = catalogos.casado;
        data.union = catalogos.union;
        data.fecha_nacimiento = generic.dateToString(data.fecha_nacimiento);
        data.tipoFrmEmpresa = catalogos?.tipoFrmEmpresa;
        if (catalogos?.tipoFrmEmpresa === 'FRM_DIFARE') {
            data.apoderado = catalogos?.apoderado;
        }

        const request = getValues('id') == null ? declaranteService.newProveedor(data) : declaranteService.editarProveedor(data);

        request.then(res => {
            setDialogDeclarante(false);
            loadLazyData();
            generic.funcSuccess(res, toast);
        }).catch(error => {
            generic.funcError(error, toast, setError);
        });
    };

    const editDeclarante = (idDeclarante) => {
        const iniciar = catalogos == null ? 1 : 0; // Se mantiene la lógica de iniciar por ser usado en Persona Juridica, no es necesario aquí
        declaranteService.consultarProveedor(idDeclarante, 'proveedor', id, iniciar).then(res => {
            const _cat = catalogos;
            const _dec = res.data.declarante;
            const lista = {
                tipo_declarante: _cat.declarante, tipo_identificacion: _cat.tipo_identificacion,
                nacionalidad: _cat.nacionalidad, genero: _cat.genero, estado_civil: _cat.estado_civil, pais: _cat.pais,
            };

            if (_dec.pais === _cat.ecuador.cat_id) {
                lista['provincia'] = _cat.provincias;
                lista['ciudad'] = res.data.ciudades;
                setCiudades(res.data.ciudades);
            }

            if (tipoPersona) {
                _dec.tipo_persona = tipoPersona;
            }

            if (_cat?.tipoFrmEmpresa === 'FRM_DIFARE' && _dec.tipo_declarante === _cat.apoderado?.cat_id) {
                lista['tipo_identificacion_cy'] = _cat.tipo_identificacion;
                lista['nacionalidad_cy'] = _cat.nacionalidad;
            }

            Object.keys(lista).forEach(key => {
                if (_dec[key] != null) {
                    _dec[key] = lista[key].find(el => el.cat_id === _dec[key]) || _dec[key];
                }
            });
            setSiEstadoCivil(_dec.tipo_declarante === _cat.apoderado?.cat_id || !esTipoEspecial);

            _dec.fecha_nacimiento = generic.stringToDate(_dec.fecha_nacimiento);

            // Setear estados visuales
            setEsRuc(_dec.tipo_identificacion?.cat_id === _cat.ruc?.cat_id);
            setEsCedula(_dec.tipo_identificacion?.cat_id === _cat.cedula?.cat_id);
            setEcuador(_dec.pais?.cat_id === _cat.ecuador?.cat_id);
            setTieneConyugue(_dec.estado_civil?.cat_id === _cat.casado?.cat_id || _dec.estado_civil?.cat_id === _cat.union?.cat_id);

            reset(generic.renewValues(_dec, omodel.model));
            setDialogDeclarante(true);
        });
    };

    const confirmDelete = (idDeclarante) => {
        confirmDialog({
            message: '¿Está seguro de eliminar el declarante?',
            header: 'Confirmación',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                declaranteService.eliminarDeclarante(idDeclarante, 'proveedor', id).then(res => {
                    loadLazyData();
                    generic.funcSuccess(res, toast);
                }).catch(error => generic.funcDeleteError(error, toast));
            }
        });
    };

    const onChangeTipo = (e) => {
        setEsRuc(JSON.stringify(e.value) === JSON.stringify(catalogos.ruc));
        setEsCedula(JSON.stringify(e.value) === JSON.stringify(catalogos.cedula));
        clearErrors('identificacion');
    };

    const onChangePais = (e) => setEcuador(e.value?.cat_id === catalogos?.ecuador?.cat_id);

    const onChangeEstadoCivil = (e) => {
        setTieneConyugue(e.value?.cat_id === catalogos?.casado?.cat_id || e.value?.cat_id === catalogos?.union?.cat_id);
    };

    const onChangeProvincia = (e) => {
        if (e.value) {
            catalogoService.getCatalogosId(e.value.cat_id).then(res => setCiudades(res.data));
        }
    };

    const itemsMenu = () => {
        if (soloLectura) {
            return [{
                label: 'Opciones',
                items: [
                    { label: 'Ver detalle', icon: 'pi pi-eye', command: () => editDeclarante(declaranteSeleccionado?.id) }
                ]
            }];
        }

        // Opciones estandar
        return [{
            label: 'Opciones',
            items: [
                { label: 'Editar', icon: 'pi pi-pencil', command: () => editDeclarante(declaranteSeleccionado?.id) },
                { label: 'Eliminar', icon: 'pi pi-trash', command: () => confirmDelete(declaranteSeleccionado?.id) }
            ]
        }];
    };

    const accionesTemplate = (rowData) => (
        <Button icon="pi pi-caret-down" type="button" className="p-button-rounded p-button-success mr-2" title="Opciones/Acciones" aria-controls="popup_menu" aria-haspopup
            onClick={(event) => { setDeclaranteSeleccionado(rowData); menuRef.current.toggle(event); }}
        />
    );

    const ColumnaAcciones = (
        <Column header="Acciones" body={accionesTemplate} style={{ width: '4rem', textAlign: 'center' }} frozen alignFrozen="right" />
    );

    const cabeceraTabla = () => {
        return (
            <div className="flex align-items-center justify-content-start gap-3 py-1">
                {permss?.crear && id && !soloLectura && ((tablaEstado.totalRecords === 0 && catalogos?.tipoFrmEmpresa === 'FRM_TOYOTA')
                    || catalogos?.tipoFrmEmpresa !== 'FRM_TOYOTA') && (
                        <Button icon="pi pi-plus" className="p-button-success p-button-sm" type="button" onClick={openNew} />
                    )}
                <span className="text-sm font-semibold text-600 uppercase tracking-wider">
                    Listado de Representantes / {titulo} Registrados
                </span>
            </div>
        );
    };
    const tipoDoc = esRuc ? 'IDEN_RUC' : esCedula ? 'IDEN_CEDULA' : null;

    const reglaValidacion = (val) => {
        // Si no es RUC ni Cédula (ej. Análogo u otros), es válido por defecto
        if (!tipoDoc) return true;
        return generic.validarIdentificacionEcuador(val, tipoDoc) || `${esRuc ? 'RUC' : 'Cédula'} incorrecto`;
    };

    const onError = (errors) => {
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Se detectaron errores, por favor revise el formulario para corregir.', life: 3000 });
    };

    const onChangeTipoDeclarante = (e) => {
        if (e.value?.cat_id === catalogos?.apoderado?.cat_id || !esTipoEspecial) {
            setSiEstadoCivil(true);
        } else {
            setSiEstadoCivil(false);
        }
    };

    return (
        <div className="componente-declarante">
            <Menu model={itemsMenu()} popup ref={menuRef} id="popup_menu" />
            <TablaDinamica modelo={omodel.model} estadoTabla={tablaEstado} datos={tablaEstado.data} totalRegistros={tablaEstado.totalRecords} dataKey="id" acciones={ColumnaAcciones} header={cabeceraTabla()} />
            <Dialog visible={dialogDeclarante} header={getValues('id') == null ? `Nuevo representante/${titulo}` : `Editar representante/${titulo}`} modal className="p-fluid modal-ssize" onHide={hideDialog} style={{ width: '750px' }}>
                <form onSubmit={(e) => { e.stopPropagation(); handleSubmit(onSubmit, onError)(e) }}>
                    <div className="formgrid grid px-2">

                        <div className="col-12 mb-3">
                            <div className="flex align-items-center">
                                <span className="text-800 font-bold text-base mr-3 flex align-items-center gap-2">
                                    <i className="pi pi-user text-primary"></i>
                                    DATOS PERSONALES Y DE IDENTIDAD
                                </span>
                                <div className="border-bottom-1 border-300 flex-grow-1" style={{ height: '1px' }}></div>
                            </div>
                        </div>

                        {inDrop('tipo_declarante', catalogos?.declarante, {
                            lab: omodel.optionLabel, chn: onChangeTipoDeclarante,
                            ne: tipoPersona !== null && catalogos?.tipoFrmEmpresa === 'FRM_TOYOTA'
                        }, 4)}
                        {inDrop('tipo_identificacion', catalogos?.tipo_identificacion, { lab: omodel.optionLabel, chn: onChangeTipo }, 4)}
                        {inText('identificacion', { validate: reglaValidacion, icon: 'pi pi-id-card' }, 4)}

                        {esRuc && inText('razon_social', { icon: 'pi pi-building' }, 8)}
                        {!esRuc && inText('nombres', { icon: 'pi pi-user' }, 4)}
                        {!esRuc && inText('apellidos', { icon: 'pi pi-user' }, 4)}
                        {inCalendar('fecha_nacimiento', {}, 4)}

                        {inDrop('nacionalidad', catalogos?.nacionalidad, { lab: omodel.optionLabel }, 4)}
                        {inDrop('genero', catalogos?.genero, { lab: omodel.optionLabel }, 4)}
                        {catalogos?.tipoFrmEmpresa !== 'FRM_TOYOTA' && siEstadoCivil &&
                            inDrop('estado_civil', catalogos?.estado_civil, { lab: omodel.optionLabel, chn: onChangeEstadoCivil }, 4)}

                        <div className="col-12 my-3">
                            <div className="flex align-items-center">
                                <span className="text-800 font-bold text-base mr-3 flex align-items-center gap-2">
                                    <i className="pi pi-map-marker text-primary"></i>
                                    UBICACIÓN Y DATOS DE CONTACTO
                                </span>
                                <div className="border-bottom-1 border-300 flex-grow-1" style={{ height: '1px' }}></div>
                            </div>
                        </div>

                        {inDrop('pais', catalogos?.pais, { lab: omodel.optionLabel, chn: onChangePais, val: false }, 4)}
                        {ecuador && inDrop('provincia', catalogos?.provincias, { lab: omodel.optionLabel, chn: onChangeProvincia }, 4)}
                        {ecuador && inDrop('ciudad', ciudades, { lab: omodel.optionLabel }, 4)}
                        {!ecuador && inText('canton', { icon: 'pi pi-map-marker' }, 8)}

                        {inArea('direccion_domicilio', 2, 100, 12)}

                        {inText('telefono_celular', { icon: 'pi pi-mobile' }, 6)}
                        {inText('telefono_fijo', { icon: 'pi pi-phone' }, 6)}

                        {tieneConyugue && (
                            <>
                                <div className="col-12 my-3">
                                    <div className="flex align-items-center">
                                        <span className="text-800 font-bold text-base mr-3 flex align-items-center gap-2">
                                            <i className="pi pi-heart text-primary"></i>
                                            DATOS DEL CÓNYUGE / PAREJA
                                        </span>
                                        <div className="border-bottom-1 border-300 flex-grow-1" style={{ height: '1px' }}></div>
                                    </div>
                                </div>

                                {inText('nombres_conyugue', { icon: 'pi pi-user' }, 4)}
                                {inText('apellidos_conyugue', { icon: 'pi pi-user' }, 4)}
                                {catalogos?.tipoFrmEmpresa === 'FRM_DIFARE' &&
                                    inDrop('tipo_identificacion_cy', catalogos?.tipo_identificacion, { lab: omodel.optionLabel, chn: onChangeTipo }, 4)}
                                {inText('identificacion_conyugue', { icon: 'pi pi-id-card' }, 4)}
                                {catalogos?.tipoFrmEmpresa === 'FRM_DIFARE' &&
                                    inDrop('nacionalidad_cy', catalogos?.nacionalidad, { lab: omodel.optionLabel }, 4)}
                            </>
                        )}

                        <div className="col-12 mt-4 flex justify-content-end">
                            {soloLectura ? (
                                <div className="field col">
                                    <Button label="Cerrar" style={{ maxWidth: '120px' }} icon="pi pi-times" type="button" className="p-button-warning" onClick={() => hideDialog(false)} />
                                </div>
                            ) : (
                                generic.buttonsForms(hideDialog)
                            )}
                        </div>

                    </div>
                </form>
            </Dialog>
        </div>
    );
};